import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const Ctx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eos_user") || "null"); } catch { return null; }
  });
  // Personas and roles are different vocabularies: personas are founder /
  // support_manager / advisor, roles are owner / admin / operator / viewer.
  // Deriving one from the other left the Radar's headline question blank for
  // anyone whose stored persona was missing, because PERSONA_QUESTION has no
  // entry for "owner".
  const [persona, setPersona] = useState(
    () => localStorage.getItem("eos_persona") || "founder"
  );
  const [personas, setPersonas] = useState([]);
  const [tenants, setTenants] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eos_tenants") || "[]"); } catch { return []; }
  });
  const [tenantMode, setTenantMode] = useState(() => user?.environment || "demo");
  // Permissions come from the server, never from the stored user object:
  // eos_user is client-editable, so gating a route on it would be a suggestion
  // rather than a control. The backend re-checks on every request regardless —
  // this only decides what to render.
  const [permissions, setPermissions] = useState([]);
  // False until the first /auth/me settles. Without it every permission-gated
  // route renders a redirect on first paint, before the answer has arrived.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) { setReady(true); return; }
    // A platform admin may hold no membership anywhere, in which case /auth/me
    // has no tenant to resolve and refuses. That is not an error state: they
    // simply have no tenant permissions, and /admin does not need any.
    if (!user.tenant_id) { setPermissions([]); setReady(true); return; }
    api.get("/auth/me")
      .then((r) => setPermissions(r.data?.permissions || []))
      .catch(() => setPermissions([]))
      .finally(() => setReady(true));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/personas").then((r) => setPersonas(r.data)).catch(() => {});
    // Keep the current mode if the response is not what we expect, rather
    // than blanking the header pill.
    api.get("/auth/admin/mode")
      .then((r) => r?.data?.mode && setTenantMode(r.data.mode))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (persona) localStorage.setItem("eos_persona", persona);
  }, [persona]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // v1 returns identity plus the tenants this user actually holds a
    // membership in. The active tenant is still resolved server-side on every
    // request; nothing here is authority.
    // Prefer the live tenant on sign-in; demo is something you switch into.
    const all = data.tenants || [];
    const tenant = all.find((t) => t.environment === "live") || all[0] || null;
    const user = {
      id: data.user_id,
      email: data.email,
      name: data.name,
      role: tenant?.role || "viewer",
      tenant_id: tenant?.id || null,
      tenant_name: tenant?.name || null,
      environment: tenant?.environment || "demo",
      pair_key: tenant?.pair_key || null,
      // Navigation only. See the note on the permissions state above.
      is_platform_admin: !!data.is_platform_admin,
    };
    localStorage.setItem("eos_token", data.access_token);
    localStorage.setItem("eos_user", JSON.stringify(user));
    localStorage.setItem("eos_tenants", JSON.stringify(all));
    if (tenant?.id) localStorage.setItem("eos_tenant", tenant.id);
    setUser(user);
    setTenants(all);
    setTenantMode(tenant?.environment || "demo");
    setPersona("founder");
    // fetch mode
    try {
      const r = await api.get("/auth/admin/mode");
      setTenantMode(r.data.mode);
    } catch {}
    return data.user;
  };

  const logout = () => {
    ["eos_token", "eos_user", "eos_tenant", "eos_tenants"].forEach((k) =>
      localStorage.removeItem(k)
    );
    setUser(null);
  };

  const refreshMode = async () => {
    try {
      const r = await api.get("/auth/admin/mode");
      setTenantMode(r.data.mode);
      return r.data.mode;
    } catch { return tenantMode; }
  };

  // Demo and live are separate tenants with separate data, not a flag on one
  // tenant. Switching "mode" therefore switches tenant, which goes through
  // membership validation on the server like any other tenant switch.
  const pairedTenant = tenants.find(
    (t) => t.pair_key && t.pair_key === user?.pair_key && t.environment !== tenantMode
  );

  const switchMode = async () => {
    if (!pairedTenant) return tenantMode;
    localStorage.setItem("eos_tenant", pairedTenant.id);
    const next = {
      ...user,
      tenant_id: pairedTenant.id,
      tenant_name: pairedTenant.name,
      environment: pairedTenant.environment,
      role: pairedTenant.role,
      pair_key: pairedTenant.pair_key,
    };
    localStorage.setItem("eos_user", JSON.stringify(next));
    setUser(next);
    setTenantMode(pairedTenant.environment);
    // Reload so every screen refetches under the new tenant rather than
    // showing another tenant's data until the next navigation.
    window.location.reload();
    return pairedTenant.environment;
  };

  const value = useMemo(
    () => ({ user, persona, setPersona, personas, login, logout, tenantMode,
             switchMode, refreshMode, tenants, pairedTenant,
             permissions, ready, isPlatformAdmin: !!user?.is_platform_admin,
             // A staff-only account holds no membership, so there is no
             // workspace for it to land in.
             hasWorkspace: !!user?.tenant_id }),
    [user, persona, personas, tenantMode, tenants, pairedTenant, permissions, ready]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
